/* Roles & Permissions — grid of role cards with permission coverage, user
   counts and create/edit/delete. */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import s from "./userManagement.module.css";
import {
  IoAddOutline,
  IoArrowBack,
  IoTrashOutline,
  IoShieldCheckmarkOutline,
  IoPeopleOutline,
} from "react-icons/io5";
import { listRoles, deleteRole } from "../../rbac/rbacService";
import { useRbac } from "../../rbac/RbacContext";
import { useTenant } from "../../context/TenantContext";
import { countGranted, TOTAL_PERMISSION_COUNT, summarizeModules } from "../../rbac/permissions";
import { showToast } from "../../utils/Toastify/Toastify";
import { Spinner, EmptyState, ConfirmDialog } from "./uiBits";

const RolesList = () => {
  const navigate = useNavigate();
  const { refreshRoles } = useRbac();
  const { hasFeature } = useTenant();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listRoles();
      setRoles(data);
    } catch {
      showToast("Failed to load roles", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The Creator system role is the content-partner account type — hide it
  // when the tenant doesn't have the content partners portal.
  const visibleRoles = hasFeature("content_partners_portal")
    ? roles
    : roles.filter((r) => !(r.system && r.name === "Creator"));

  const runDelete = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      await deleteRole(confirm.id);
      showToast("Role deleted", "success");
      setConfirm(null);
      load();
      refreshRoles();
    } catch (err) {
      showToast(err.message || "Failed to delete role", "error");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className={s.page}>
      <button className={s.backLink} onClick={() => navigate("/mainlayout/user-management")}>
        <IoArrowBack /> Back to users
      </button>

      <div className={s.header}>
        <div className={s.headerTitleWrap}>
          <h1 className={s.title}>Roles &amp; Permissions</h1>
          <p className={s.subtitle}>
            Define reusable permission sets and assign them to users.
          </p>
        </div>
        <div className={s.headerActions}>
          <button
            className={s.btnPrimary}
            onClick={() => navigate("/mainlayout/roles/new")}
          >
            <IoAddOutline /> Create role
          </button>
        </div>
      </div>

      {loading ? (
        <Spinner label="Loading roles…" />
      ) : roles.length === 0 ? (
        <EmptyState
          icon={<IoShieldCheckmarkOutline />}
          title="No roles yet"
          message="Create your first role to start controlling access."
          action={
            <button
              className={s.btnPrimary}
              onClick={() => navigate("/mainlayout/roles/new")}
            >
              <IoAddOutline /> Create role
            </button>
          }
        />
      ) : (
        <div className={s.roleGrid}>
          {visibleRoles.map((role) => {
            const granted = countGranted(role.permissions);
            const pct = Math.round((granted / TOTAL_PERMISSION_COUNT) * 100);
            const userCount = role.userCount || 0;
            return (
              <div key={role.id} className={s.roleCard}>
                <div className={s.roleCardTop}>
                  <div className={s.roleCardId}>
                    <span
                      className={s.roleSwatch}
                      style={{ background: role.color }}
                    >
                      <IoShieldCheckmarkOutline />
                    </span>
                    <div>
                      <div className={s.roleCardName}>
                        {role.name}
                        {role.system && <span className={s.systemTag}>System</span>}
                      </div>
                      <div className={s.roleCardStat}>
                        {summarizeModules(role.permissions)}
                      </div>
                    </div>
                  </div>
                  {/* Only Delete lives here. Editing/viewing is the bottom
                      "Edit"/"View" button — a single, unambiguous action per
                      card (system roles have no top action at all). */}
                  {!role.system && (
                    <div className={s.rowActions}>
                      <button
                        className={`${s.iconBtn} ${s.iconBtnDanger}`}
                        title="Delete"
                        onClick={() => setConfirm(role)}
                      >
                        <IoTrashOutline />
                      </button>
                    </div>
                  )}
                </div>

                <div className={s.roleCardDesc}>{role.description || "—"}</div>

                <div>
                  <div
                    className={s.roleCardStat}
                    style={{ justifyContent: "space-between", marginBottom: 6 }}
                  >
                    <span>
                      <strong>{granted}</strong>/{TOTAL_PERMISSION_COUNT} permissions
                    </span>
                    <span>{pct}%</span>
                  </div>
                  <div className={s.permBar}>
                    <div className={s.permBarFill} style={{ width: `${pct}%` }} />
                  </div>
                </div>

                <div className={s.roleCardMeta}>
                  <span className={s.roleCardStat}>
                    <IoPeopleOutline />
                    <strong>{userCount}</strong> user{userCount === 1 ? "" : "s"}
                  </span>
                  <button
                    className={s.btnSubtle}
                    onClick={() => navigate(`/mainlayout/roles/${role.id}/edit`)}
                  >
                    {role.system ? "View" : "Edit"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        danger
        title="Delete role?"
        message={`Delete the "${confirm?.name}" role? Users assigned to it must be reassigned first.`}
        confirmLabel="Delete role"
        loading={confirmLoading}
        onConfirm={runDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default RolesList;
